import React from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';

const SOURCE_LABELS = {
  auto: 'Auto Search',
  single: 'Single Query',
  scheduled: 'Scheduled',
};

const STATUS_COLORS = {
  running: 'primary',
  completed: 'success',
  failed: 'error',
  pending: 'default',
  cancelled: 'default',
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getElapsedTime = (startTime, endTime) => {
  if (!startTime) return null;
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const elapsed = end - start;

  const hours = Math.floor(elapsed / 3600000);
  const minutes = Math.floor((elapsed % 3600000) / 60000);
  const seconds = Math.floor((elapsed % 60000) / 1000);

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatUsd = (value) => {
  const amount = Number(value || 0);
  return amount > 0 ? `$${amount.toFixed(4)}` : null;
};

const getStatusIcon = (status) => {
  if (status === 'running') return <CircularProgress size={16} />;
  if (status === 'completed') return <CheckCircleIcon fontSize="small" color="success" />;
  if (status === 'failed') return <ErrorIcon fontSize="small" color="error" />;
  return <ScheduleIcon fontSize="small" />;
};

const SearchProgressList = ({
  searchRuns,
  onViewDetails,
  showHistory = false,
}) => {
  const runs = showHistory
    ? searchRuns
    : searchRuns.filter((run) => run.status === 'running');

  if (runs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {showHistory ? 'No search history yet' : 'No searches currently running'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {showHistory
            ? 'Completed searches will appear here.'
            : 'Start a search to track progress here in real time.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {runs.map((run) => (
        <Card key={run.id} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={SOURCE_LABELS[run.source] || run.source}
                  icon={run.source === 'scheduled' ? <ScheduleIcon /> : <PlayArrowIcon />}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={run.status}
                  color={STATUS_COLORS[run.status] || 'default'}
                  icon={getStatusIcon(run.status)}
                />
                {run.totalJobsFound > 0 && (
                  <Chip
                    size="small"
                    label={`${run.totalJobsFound} jobs found`}
                    color="success"
                    variant="outlined"
                  />
                )}
                {formatUsd(run.costs?.openaiUsd) && (
                  <Chip
                    size="small"
                    label={`OpenAI ${formatUsd(run.costs.openaiUsd)}`}
                    color="info"
                    variant="outlined"
                  />
                )}
                {formatUsd(run.costs?.scrapingdogUsd) && (
                  <Chip
                    size="small"
                    label={`ScrapingDog ${formatUsd(run.costs.scrapingdogUsd)}`}
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Tooltip title="View details">
                <IconButton size="small" onClick={() => onViewDetails(run.id)}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={run.progress?.percentage || 0}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                  color={run.status === 'failed' ? 'error' : 'primary'}
                />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                  {run.progress?.percentage || 0}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {run.progress?.current || 0} of {run.progress?.total || 0} queries processed
                  {run.failedQueries > 0 && ` (${run.failedQueries} failed)`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Elapsed: {getElapsedTime(run.startedAt, run.completedAt)}
                </Typography>
              </Box>
            </Box>

            {run.categorySummary?.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {run.categorySummary.map((cat) => (
                  <Chip
                    key={cat.category}
                    size="small"
                    label={`${cat.category}: ${cat.jobsFound} jobs (${cat.completed}/${cat.total})`}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Started: {formatDateTime(run.startedAt)}
              </Typography>
              {run.completedAt && (
                <Typography variant="caption" color="text.secondary">
                  Ended: {formatDateTime(run.completedAt)}
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => onViewDetails(run.id)}>
                Progress Details
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default SearchProgressList;
